import { create } from 'zustand'
import type { Citation } from './types'

interface CitationState {
  /** All citations known to the current view, keyed by id. */
  registry: Record<string, Citation>
  openCitationId: string | null
  register: (citations: Citation[]) => void
  open: (id: string) => void
  close: () => void
  /** Clear on route change so a stale drawer cannot outlive its data. */
  reset: () => void
}

/**
 * F0-29 — one drawer, one store.
 *
 * §5.2 is explicit: the citation drawer is a single app-level instance driven by
 * a store, not one drawer per claim. A 400-claim knowledge feed (SRE-FR-004 on a
 * 15-question brief) would otherwise mount 400 portals, 400 focus traps and 400
 * sets of listeners — which is both a performance and an accessibility failure.
 */
export const useCitationStore = create<CitationState>((set) => ({
  registry: {},
  openCitationId: null,
  register: (citations) =>
    set((state) => {
      const next = { ...state.registry }
      for (const citation of citations) next[citation.id] = citation
      return { registry: next }
    }),
  open: (id) => set({ openCitationId: id }),
  close: () => set({ openCitationId: null }),
  reset: () => set({ registry: {}, openCitationId: null }),
}))

/** Selector for the currently open citation, if any. */
export function useOpenCitation(): Citation | null {
  const openId = useCitationStore((s) => s.openCitationId)
  const registry = useCitationStore((s) => s.registry)
  return openId ? registry[openId] ?? null : null
}
